import { toSafeExternalUrl, type ExternalUrlInput } from '@common/utils/externalUrl';
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import React, { Fragment } from 'react';
export const isServer = () => {
  return typeof window === 'undefined';
};
export { formatUserAddress, shortAddress, shortString } from '@common/utils/common';
export async function safeCopyToClipboard(value: string, tag: string = 'universalx.app') {
  const text = value;
  // 使用有效的 HTML 结构，确保文档软件能正确解析
  // 转义 HTML 特殊字符以防止 XSS 攻击
  const escapedText = text.replace(/&/g, '&amp;') // 将 & 替换为 &amp;，防止被解析为 HTML 实体或标签的开始
  .replace(/</g, '&lt;') // 将 < 替换为 &lt;，防止被解析为 HTML 标签的开始
  .replace(/>/g, '&gt;') // 将 > 替换为 &gt;，防止被解析为 HTML 标签的结束
  .replace(/"/g, '&quot;') // 将双引号 " 替换为 &quot;，防止破坏 HTML 属性值
  .replace(/'/g, '&#39;'); // 将单引号 ' 替换为 &#39;，防止破坏 HTML 属性值
  const html = `<!--data-source=${tag}--><span data-source="${tag}">${escapedText}</span>`;
  if (navigator.clipboard) {
    try {
      await navigator.clipboard.write([new ClipboardItem({
        'text/html': new Blob([html], {
          type: 'text/html'
        }),
        'text/plain': new Blob([text], {
          type: 'text/plain'
        })
      })]);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}
export async function safeReadClipboard() {
  if (navigator.clipboard) {
    try {
      const items = await navigator.clipboard.read();
      const result: {
        text?: string;
        html?: string;
      } = {};
      for (const item of items) {
        // 检查是否有 text/plain 类型
        if (item.types.includes('text/plain')) {
          const blob = await item.getType('text/plain');
          result.text = await blob.text();
        }

        // 检查是否有 text/html 类型
        if (item.types.includes('text/html')) {
          const blob = await item.getType('text/html');
          result.html = await blob.text();
        }
      }
      return result;
    } catch {
      return null;
    }
  }
  return null;
}

// 基础用法 - 字符串/数字替换
// formatLocale("Hello {0}!", "World")
// 输出: "Hello World!"

// React 节点替换
// formatLocale("Click {0} to continue", <Button>here</Button>)
// 输出: ["Click ", <Button>here</Button>, " to continue"]
type FormatValue<T> = T extends string | number | boolean ? string : React.ReactNode[];
export function formatLocale<T extends React.ReactNode>(txt: string, ...values: T[]): FormatValue<T> {
  const nodePlacer = '<></>';
  const nodes: React.ReactNode[] = [];
  const formatValue = txt.replace(/\{\d+\}/g, match => {
    const index = parseInt(match.slice(1, -1), 10);
    const value = values[index];
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || !value) {
      return values[index]?.toString() ?? match;
    }
    nodes.push(value);
    return nodePlacer;
  });
  if (nodes.length > 0) {
    const strs = formatValue.split(nodePlacer);
    const elements: React.ReactNode[] = [];
    for (let i = 0; i < strs.length; i++) {
      elements.push(strs[i]);
      if (nodes[i]) {
        elements.push(nodes[i]);
      }
    }
    return elements.map((item, index) => <Fragment key={index}>{item}</Fragment>) as FormatValue<T>;
  }
  return formatValue as FormatValue<T>;
}

/**
 * 计算文字在指定宽度下的最大字号
 * @param text 要显示的文字
 * @param width 容器宽度(px)
 * @param options 可选配置
 * @returns 计算出的最佳字号(px)
 */
export const calculateOptimalFontSize = (text: string, width: number, options?: {
  padding?: number; // 容器内边距
  buffer?: number; // 安全边界值
}): number => {
  const {
    padding = 0,
    buffer = 2 // 默认留2px的安全边界
  } = options || {};
  const availableWidth = width - padding * 2;

  // 创建 canvas 用于测量文字
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Cannot get canvas context');
  }

  // 获取当前页面默认字体
  const fontFamily = getComputedStyle(document.body).fontFamily || 'sans-serif';

  // 二分查找的范围,从1px到200px
  let minSize = 1;
  let maxSize = 200;
  let bestSize = 1;
  while (minSize <= maxSize) {
    const midSize = Math.floor((minSize + maxSize) / 2);

    // 设置字体
    context.font = `${midSize}px ${fontFamily}`;

    // 测量文字宽度
    const textWidth = context.measureText(text).width;
    if (textWidth <= availableWidth - buffer) {
      bestSize = midSize;
      minSize = midSize + 1;
    } else {
      maxSize = midSize - 1;
    }
  }
  return bestSize;
};
export const openLink = (url: string, target = '_blank') => {
  window.open(url, target);
};

/**
 * Open a URL that came from untrusted data (token socials, blacklist entries,
 * backend-labelled profiles) in a new tab. Non-http(s) values are dropped so a
 * `javascript:` "website" can never run in the app's origin, and the tab is
 * opened without an opener. Returns whether anything was opened.
 */
export const openExternalUrl = (url: ExternalUrlInput): boolean => {
  const safeUrl = toSafeExternalUrl(url);
  if (!safeUrl) return false;
  window.open(safeUrl, '_blank', 'noopener,noreferrer');
  return true;
};
export const truncateText = (text: string, maxLength = 10, appendEllipsis = true): string => {
  const characters = Array.from(text);
  let length = 0;
  let result = '';
  for (const char of characters) {
    // 判断是否是 emoji
    const isEmoji = /\p{Extended_Pictographic}/u.test(char);
    length += isEmoji ? 2 : 1;
    if (length > maxLength) {
      return appendEllipsis ? result + '...' : result;
    }
    result += char;
  }
  return result;
};
export function getVisibleHeight() {
  const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

  // 减去安全边距(可根据需要调整)
  const safeMargin = 20; // 顶部和底部各预留10px
  const bottomBar = 50; // 移动端底部导航栏高度

  return height - safeMargin - bottomBar;
}

/**
 *  解析JSON字符串
 * @param jsonString
 * @returns
 */
export function parseJSON(jsonString: string): Record<string, unknown> | null {
  try {
    return JSON.parse(jsonString);
  } catch {
    return null;
  }
}

/**
 * Safely parse a JSON string that may be null, undefined, empty,
 * or the literal string "undefined" (which JSON.parse rejects).
 */
export function safeParseJson<T>(value: string | null | undefined, fallback: T): T {
  if (value == null || value === '' || value === 'undefined') {
    return fallback;
  }
  try {
    const parsed = JSON.parse(value) as T | null;
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * 16进制转成rgba
 * @param hex 16进制
 * @param alpha 透明度
 * @returns 带有透明度的rgba
 */
export const hexToRgba = (hex: string, alpha = 1): string => {
  hex = hex.replace('#', '');
  // 验证透明度值是否在0到1之间
  if (hex.length !== 6) {
    throw new Error('Invalid hex color code. It should be 6 characters long.');
  }

  // 验证透明度值是否在0到1之间
  if (alpha < 0 || alpha > 1) {
    throw new Error('Alpha value must be between 0 and 1.');
  }

  // 解析十六进制颜色代码为RGB值
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // 返回rgba格式的颜色值
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
export function px2rem(px: number) {
  return (px / 16).toFixed(2) + 'rem';
}

// Only `replace` is used; the web router is the react-router shim, not a full AppRouterInstance
export const routerReplace = (router: Pick<AppRouterInstance, 'replace'>, pathname: string, query: Record<string, string | number>, callback?: (params: URLSearchParams) => URLSearchParams) => {
  const currentParams = new URLSearchParams(window.location.search);
  const newParams = new URLSearchParams(window.location.search);
  Object.entries(query).forEach(([key, value]) => {
    newParams.set(key, value.toString());
  });
  callback?.(newParams);
  const currentString = currentParams.toString();
  const newString = newParams.toString();
  if (currentString !== newString) {
    router.replace(`${pathname}?${newString}`, {
      scroll: false
    });
  }
};